import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, QrCode } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";

const Services = () => {
  const { t } = useLanguage();
  const sp = t.servicesPage;

  return (
    <main data-testid="services-page">
      <section className="relative overflow-hidden bg-paper pt-32 pb-14 md:pt-44 md:pb-20">
        <div className="blueprint-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-accent">
              {sp.overline}
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {sp.title}
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">{sp.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {t.services.map((s, i) => (
            <Reveal key={s.slug} delay={0.03 * i}>
              <Link
                to={s.path}
                data-testid={`services-row-${s.slug}`}
                className="group grid items-center gap-4 border-t border-slate-200 py-7 transition-colors duration-300 hover:bg-mist md:grid-cols-12 md:gap-8 md:px-4"
              >
                <span className="font-mono text-[0.7rem] tracking-[0.3em] text-slate-400 md:col-span-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="font-display text-xl font-semibold tracking-tight text-ink transition-colors duration-300 group-hover:text-navy md:col-span-4 md:text-2xl">
                  {s.title}
                </h2>
                <p className="text-sm leading-relaxed text-slate-500 md:col-span-6">{s.short}</p>
                <span className="hidden justify-self-end rounded-full border border-slate-200 p-3 text-navy transition-all duration-300 group-hover:border-navy group-hover:bg-navy group-hover:text-white md:block">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div
              data-testid="services-certificoqr-section"
              className="relative overflow-hidden rounded-2xl bg-[#0A0F1A] p-8 text-white shadow-2xl ring-1 ring-white/10 md:p-14"
            >
              <div
                className="pointer-events-none absolute -right-20 -bottom-20 h-72 w-72 opacity-[0.12]"
                aria-hidden="true"
              >
                <svg viewBox="0 0 100 100" fill="none" className="h-full w-full">
                  {Array.from({ length: 8 }).map((_, r) =>
                    Array.from({ length: 8 }).map((_, cIdx) =>
                      (r * 8 + cIdx) % 3 !== 0 ? (
                        <rect key={`${r}-${cIdx}`} x={cIdx * 12.5} y={r * 12.5} width="9" height="9" fill="#10B981" />
                      ) : null
                    )
                  )}
                </svg>
              </div>
              <div className="relative grid gap-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-emerald-400">
                    <QrCode className="h-3.5 w-3.5" />
                    {sp.certifico.badge}
                  </span>
                  <h2 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                    {sp.certifico.title}
                  </h2>
                  <p className="mt-5 max-w-xl text-base leading-relaxed text-white/65">
                    {sp.certifico.text}
                  </p>
                  <a
                    href="https://certificoqr.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="services-certificoqr-cta"
                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-[#04170F] transition-colors duration-300 hover:bg-emerald-400"
                  >
                    {sp.certifico.cta}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
                <div className="lg:col-span-5">
                  <ul className="space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-7">
                    {sp.certifico.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-white/75">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={3} />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Services;
